import { useCallback, useEffect, useRef, useState } from "react";

const STORAGE_KEY = "gc_feed_streak_v1";
// Day-counts at which we celebrate. Each is shown at most once per device.
// Day 1 is the "welcome" card so first-time visitors see the streak system
// exists; subsequent thresholds reward continued visits.
const THRESHOLDS = [1, 3, 7, 14, 30, 50, 100];

interface StreakData {
  // ISO YYYY-MM-DD of the last day the user opened the feed.
  lastVisitDate: string;
  // Number of consecutive days (today inclusive).
  streak: number;
  // Largest threshold that's already been celebrated; we never show the same
  // milestone twice so it doesn't become noise.
  lastShownThreshold: number;
}

function isoDay(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function read(): StreakData {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return blank();
    const parsed = JSON.parse(raw) as Partial<StreakData>;
    if (
      typeof parsed.lastVisitDate === "string" &&
      typeof parsed.streak === "number"
    ) {
      return {
        lastVisitDate: parsed.lastVisitDate,
        streak: parsed.streak,
        lastShownThreshold: parsed.lastShownThreshold ?? 0,
      };
    }
  } catch {
    // Corrupt JSON / quota — fall through to blank.
  }
  return blank();
}

function blank(): StreakData {
  return { lastVisitDate: "", streak: 0, lastShownThreshold: 0 };
}

function write(data: StreakData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Private mode / quota — accept loss; the feed still works without it.
  }
}

export interface FeedStreakState {
  // Current streak count (1+ means user opened the feed today).
  streak: number;
  // Threshold just crossed and not yet shown — the SuggestionFeed injects a
  // celebration card when this is non-null.
  milestone: number | null;
  // Call after the user dismisses or scrolls past the celebration so we don't
  // try to re-inject it within the same session.
  consume: () => void;
  // TEST-ONLY hook: bump the visible streak by 1 and persist. Lets the
  // demo show off how the visuals intensify as streaks grow without
  // waiting days. Wired up to the streak badge as an onClick.
  bumpStreak: () => void;
}

/**
 * Tracks daily-visit streaks for the suggestion feed. Pure FE state — no
 * backend, no DB. The hook updates on mount: bump streak if we crossed a day
 * boundary, reset if we missed a day, no-op if we already counted today.
 *
 * Returns a single milestone-to-celebrate (the highest threshold the streak
 * has reached that hasn't been shown yet); SuggestionFeed renders that as a
 * streak_milestone card injected into the feed list.
 */
export function useFeedStreak(): FeedStreakState {
  const [streak, setStreak] = useState(0);
  const [milestone, setMilestone] = useState<number | null>(null);
  // React 18 Strict Mode mounts effects twice in dev. The second run would
  // see the localStorage write from the first and conclude "milestone already
  // shown" — clearing the celebration. Guard with a ref so the streak math
  // runs once per real mount.
  const initRef = useRef(false);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const today = isoDay(0);
    const yesterday = isoDay(-1);
    const data = read();

    let newStreak: number;
    if (data.lastVisitDate === today) {
      newStreak = Math.max(1, data.streak);
    } else if (data.lastVisitDate === yesterday) {
      newStreak = data.streak + 1;
    } else {
      newStreak = 1;
    }

    let nextMilestone: number | null = null;
    let newLastShown = data.lastShownThreshold;
    for (const t of THRESHOLDS) {
      if (newStreak >= t && t > data.lastShownThreshold) {
        nextMilestone = t;
        newLastShown = t;
      }
    }

    setStreak(newStreak);
    setMilestone(nextMilestone);
    write({
      lastVisitDate: today,
      streak: newStreak,
      lastShownThreshold: newLastShown,
    });
  }, []);

  const consume = () => setMilestone(null);

  const bumpStreak = useCallback(() => {
    setStreak((s) => {
      const next = s + 1;
      const data = read();
      write({
        lastVisitDate: data.lastVisitDate || isoDay(0),
        streak: next,
        lastShownThreshold: data.lastShownThreshold,
      });
      return next;
    });
  }, []);

  return { streak, milestone, consume, bumpStreak };
}
